import { Box, useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import React from 'react'
import { Legend } from 'recharts'
import { makeStyles } from 'tss-react/mui'
import PieChartContent from './PieChartContent'
import { useChartDimensions } from '../hooks'
import { getLabelPercentageValue } from 'src/Elements/SystemDataHandler/SystemChart/PieChart/utils/get-label-percentage-value'
import { TraceKey } from 'src/Libs/system-charts/shared'
import { UnitTypes } from 'src/Libs/types'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { getLocalizedNumber } from 'src/Libs/languages/functions/default'
import { formatPieChartLabel } from 'src/Libs/dynamic-power-prices/utils/cost-saving-chart'
import { isUndefined } from 'lodash'
import type { PieChartData } from './pie-chart.types'
import type { CostSavingChartTraceKey } from 'src/Libs/dynamic-power-prices/cost-saving-chart-trace-keys'

export type PieChartLegendType = Legend & React.ReactElement
export type PieChartLegendRef = React.RefObject<PieChartLegendType> | null

export interface PieChartProps {
  data: PieChartData[]
  cells: React.ReactNode[]
  savingsSum?: number
  traceNames: Record<string, string>
  height?: number
  width?: number
  legendHeight?: number
  headerHeight?: number
  title?: string
  labelTraces?: TraceKey[] | CostSavingChartTraceKey[]
  labelNameKey?: string | React.ReactElement
  isCostSavingsPieChart?: boolean
  isWeightedAveragePriceOn?: boolean
  weightedPrice?: number
  labelAction?: React.ReactElement
  icon?: React.ReactElement
  showFootnoteAsterix?: boolean //To be used if there is a footnote for the pie chart value
  isInactive?: boolean
  textColor?: string
}

interface StyleProps {
  height: number
  legendSpacing: string
}
export const NO_BREAK_SPACE = '\u00A0'

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  chartContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'start',
    height: props.height,
    width: props.height,
    justifySelf: 'center',
  },
  chart: {
    display: 'flex',
    outline: 'none',
  },
}))

/*
 * This component renders the pie chart
 */
const PieChart: React.FC<PieChartProps> = ({
  data,
  cells,
  savingsSum,
  traceNames,
  height,
  width,
  legendHeight,
  headerHeight,
  title,
  labelTraces,
  labelNameKey,
  isCostSavingsPieChart = false,
  isWeightedAveragePriceOn = false,
  weightedPrice,
  labelAction,
  icon,
  showFootnoteAsterix = false,
  isInactive,
  textColor,
}) => {
  const theme = useTheme()
  const { t } = useCustomTranslation()
  const upMd = useMediaQuery(theme.breakpoints.up('md'))
  const legendSpacing = upMd ? theme.spacing(6) : theme.spacing(3)
  const { chartSize, innerRadius, outerRadius } = useChartDimensions({ height, width, legendHeight, headerHeight })
  const { classes } = useStyles({ height: chartSize ?? 0, legendSpacing })

  const value = getLabelPercentageValue(data, traceNames, labelTraces)
  const formattedWeightedPrice = isWeightedAveragePriceOn && weightedPrice !== undefined ? formatPieChartLabel(weightedPrice) : ''
  const localizedValue = getLocalizedNumber(value ?? 0, undefined, undefined, true)
  const valueToUse = isWeightedAveragePriceOn ? formattedWeightedPrice : localizedValue
  const compactSavingsSum = !isUndefined(savingsSum) ? formatPieChartLabel(savingsSum) : undefined

  const valueAndUnit = getValueAndUnitToDisplay(isWeightedAveragePriceOn, isCostSavingsPieChart, compactSavingsSum, valueToUse)
  const isLabelValueNamePairValid = (compactSavingsSum ?? localizedValue) || value

  const name = isWeightedAveragePriceOn ? t('chart.pie-chart.home-consumption.weighted-average-price-label') : labelNameKey
  const labelValueNamePair = isLabelValueNamePairValid
    ? { name, value: valueAndUnit[0] ?? '', additionalUnit: ` ${valueAndUnit[1]}`, showAsterix: showFootnoteAsterix }
    : undefined
  const dataForDisplay = data.filter(item => item.value && item.value > 0)

  return (
    <Box className={classes.chartContainer} data-qa={`${title}-pie-chart`}>
      <PieChartContent
        chartHeight={chartSize}
        chartWidth={chartSize}
        cells={cells}
        data={dataForDisplay}
        labelValueNamePair={labelValueNamePair}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        chartClassName={classes.chart}
        icon={icon}
        labelAction={labelAction}
        isInactive={isInactive}
        textColor={textColor}
      />
    </Box>
  )
}

/**
 * Function to get the value and separate unit displayed in the pie chart
 * @param isWeightedAveragePriceOn
 * @param isCostSavingsPieChart
 * @param savingsSum
 * @param value
 * @returns
 */
const getValueAndUnitToDisplay = (
  isWeightedAveragePriceOn: boolean,
  isCostSavingsPieChart: boolean,
  savingsSum?: string,
  value?: string
): [string | undefined, UnitTypes] => {
  if (isCostSavingsPieChart && savingsSum) return [savingsSum, UnitTypes.euros]
  if (isWeightedAveragePriceOn && value !== undefined) return [value, UnitTypes.cents]
  return [value, UnitTypes.percentage]
}

export default PieChart
